/**
 * LOT 536 Phase 28 (Validation Daniel 03/06/2026) — Redimensionnement
 * on-the-fly des images servies via `/api/files/*`.
 *
 * Le backend (`routes/public.py`) supporte déjà 3 query params :
 *   ?w=<int 64-2400>  : largeur cible en px (snapping vers buckets serveur)
 *   ?q=<int 10-100>   : qualité (default 85 côté serveur)
 *   ?fmt=auto|webp|original : format de sortie (WebP économise ~50%)
 *
 * Bénéfices :
 *   - Galerie 6 thumbs : 6 × 500 KB = 3 MB → 6 × ~25 KB = 150 KB (×20)
 *   - Lightbox plein écran : transparent (utilise l'URL originale)
 *   - Cache-Control: public, max-age=86400, immutable → cache 24h
 *   - ETag stable → 304 Not Modified sur revisite
 *
 * Cible : uniquement les URLs `/api/files/*` (les images du storage objet
 * post-migration). Les URLs absolues (Unsplash, etc.) et les URLs `data:`
 * sont retournées telles quelles.
 */
import imageVariants from '../generated/image-variants.json';

/**
 * Génère une URL avec resize on-the-fly.
 *
 * @param {string} url       — URL originale (peut être /api/files/... ou absolue)
 * @param {object} opts      — { w, q, fmt }
 * @returns {string}         — URL transformée (ou identique si pas optimisable)
 */
export function optimizeImageUrl(url, { w, q, fmt } = {}) {
  if (!url || typeof url !== 'string') return url;

  // URLs non optimisables : data:, blob:, mailto:, etc.
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  // Détecter si c'est une URL /api/files/* (avec ou sans origin)
  // Les URLs d'autres origines (Unsplash, etc.) ne supportent pas nos params.
  const isApiFiles =
    url.includes('/api/files/') &&
    (url.startsWith('/api/files/') || url.includes('/api/files/'));

  if (!isApiFiles) return url;

  // Construction URL avec params (préserve les query existants éventuels)
  const sep = url.includes('?') ? '&' : '?';
  const params = [];
  if (w !== undefined && w !== null) params.push(`w=${encodeURIComponent(w)}`);
  if (q !== undefined && q !== null) params.push(`q=${encodeURIComponent(q)}`);
  if (fmt !== undefined && fmt !== null) params.push(`fmt=${encodeURIComponent(fmt)}`);
  if (params.length === 0) return url;
  return `${url}${sep}${params.join('&')}`;
}

/**
 * Variants utilitaires pour les usages les plus fréquents.
 */
export const IMG_THUMB = { w: 400, q: 80, fmt: 'webp' };       // grille galerie
export const IMG_PREVIEW = { w: 800, q: 85, fmt: 'webp' };     // preview moyen
export const IMG_HERO = { w: 1600, q: 85, fmt: 'webp' };       // cover/hero
// (pas de IMG_FULL : pour le lightbox on garde l'URL originale)

/**
 * LOT 537 v14 (Bug Daniel 03/06/2026 — Cover image stale) — Cache-buster.
 *
 * Problème : `routes/public.py` renvoie `Cache-Control: public, max-age=86400,
 * immutable` pour les images publiques. Quand l'admin remplace une cover, le
 * backend écrase le fichier sous la MÊME clé `cover.png` → l'URL ne change
 * pas → le navigateur garde l'ancienne image en cache pendant 24h (le mot-clé
 * `immutable` désactive même la revalidation ETag).
 *
 * Solution : ajouter un query param `?v=<version>` qui change quand l'image
 * change (typiquement le timestamp `cover_image_updated_at` ou un counter
 * incrémenté après mutation). Le backend ignore ce param (pas dans la
 * whitelist resize/etag), mais le navigateur traite chaque URL distincte
 * comme une ressource distincte → cache miss → ré-chargement de la vraie
 * image fraîchement uploadée.
 *
 * @param {string} url      — URL d'origine (peut déjà avoir des params)
 * @param {string|number} version — Tag de version (timestamp ISO, counter, etc.)
 * @returns {string}        — URL avec ?v=<version> ajouté (ou identique si invalides)
 */
export function withCacheBust(url, version) {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (version === undefined || version === null || version === '') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(String(version))}`;
}

/**
 * HARDENING V1 (LOT 5) — srcset pour les IMAGES STATIQUES publiques.
 *
 * Les reportages projets (/images/<projet>/*.webp, 1920 px) disposent d'une
 * variante `<nom>.w960.webp` générée par scripts/generate_image_variants.py,
 * qui émet aussi src/generated/image-variants.json : le srcset n'est émis
 * QUE si la variante existe réellement (aucun candidat 404 possible).
 * Le navigateur ne télécharge la pleine taille que si l'affichage l'exige.
 */
export function staticImageSrcSet(url) {
  if (!url || typeof url !== 'string') return null;
  const entry = imageVariants[url];
  if (!entry) return null;
  return `${entry.variant} 960w, ${url} ${entry.width}w`;
}
